import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  ShoppingOutlined,
  SmileOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Row,
  Typography,
  Card,
  Radio,
  Result,
  Divider,
  Modal,
  Spin,
} from "antd";

import ContentTitle from "../../components/ContentTitle/ContentTitle";
import FormInput from "../../components/FormInput/FormInput";
import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import {
  selectCartItems,
  selectTotalPrice,
} from "../../redux-toolkit/cart/cart-selector";
import {
  resetOrderState,
  setOrderLoadingState,
} from "../../redux-toolkit/order/order-slice";
import { LoadingStatus } from "../../types/types";
import { resetCartState } from "../../redux-toolkit/cart/cart-slice";
import { fetchCart } from "../../redux-toolkit/cart/cart-thunks";
import OrderItem from "./OrderItem/OrderItem";
import { BASE_URL } from "../../constants/urlConstants";
import "./Order.css";

type PaymentType = "COD" | "ONLINE";
type DeliveryStatus = "AVAILABLE" | "NOT_AVAILABLE" | null;

declare global {
  interface Window {
    Razorpay?: any;
  }
}

const Order: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [form] = Form.useForm();

  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [paymentType, setPaymentType] = useState<PaymentType>("COD");
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus>(null);

  const [progress, setProgress] = useState({
    open: false,
    text: "",
  });

  const usersData = useSelector(selectUserFromUserState);
  const perfumes = useSelector(selectCartItems);
  const totalPrice = useSelector(selectTotalPrice);

  const [perfumesFromLocalStorage, setPerfumesFromLocalStorage] =
    useState<Map<number, number>>(new Map());

  /* ================= LOAD CART ================= */
  useEffect(() => {
    const stored = new Map<number, number>(
      JSON.parse(localStorage.getItem("perfumes") || "[]")
    );

    setPerfumesFromLocalStorage(stored);
    dispatch(setOrderLoadingState(LoadingStatus.LOADED));
    dispatch(fetchCart(Array.from(stored.keys())));

    if (usersData) {
      form.setFieldsValue(usersData);
    }

    return () => {
      dispatch(resetOrderState());
      dispatch(resetCartState());
    };
  }, [dispatch, form, usersData]);

  /* ================= PINCODE → DB → INDIA POST ================= */
  const fetchPincodeDetails = async (pincode: string) => {
    if (!/^\d{6}$/.test(pincode)) return;

    form.setFieldsValue({ city: "", state: "" });
    setDeliveryStatus(null);

    // 1️⃣ Check DB
    try {
      const res = await fetch(`${BASE_URL}/api/v1/order/pincode/${pincode}`);
      if (res.ok) {
        const data = await res.json();
        form.setFieldsValue({ city: data.city, state: data.state });
        setDeliveryStatus(
          data.deliveryAvailable ? "AVAILABLE" : "NOT_AVAILABLE"
        );
        return;
      }
    } catch {}

    // 2️⃣ India Post fallback
    try {
      const res = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      const data = await res.json();

      if (!data?.[0]?.PostOffice?.length) {
        setDeliveryStatus("NOT_AVAILABLE");
        return;
      }

      const po = data[0].PostOffice[0];
      form.setFieldsValue({
        city: po.District,
        state: po.State,
      });

      setDeliveryStatus(
        po.State === "Andhra Pradesh" ? "AVAILABLE" : "NOT_AVAILABLE"
      );
    } catch {
      setDeliveryStatus("NOT_AVAILABLE");
    }
  };

  /* ================= RAZORPAY LOADER ================= */
  const loadRazorpay = async (): Promise<boolean> => {
    if (window.Razorpay) return true;

    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  /* ================= PLACE ORDER ================= */
  const placeOrder = async (transactionId: string | null) => {
    const perfumesId = Object.fromEntries(
      new Map(JSON.parse(localStorage.getItem("perfumes") || "[]"))
    );

    const response = await fetch(`${BASE_URL}/api/v1/order`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form.getFieldsValue(),
        perfumesId,
        totalPrice,
        paymentType,
        transactionId,
      }),
    });

    if (!response.ok) throw new Error("Order failed");

    const result = await response.json();

    localStorage.removeItem("perfumes");
    dispatch(resetCartState());

    setOrderId(result.id);
    setOrderPlaced(true);
  };

  /* ================= FORM VALIDATION ================= */
  const isFormValid = () => {
    const values = form.getFieldsValue();
    return (
      values.firstName &&
      values.lastName &&
      values.phoneNumber &&
      values.email &&
      values.address &&
      values.postIndex &&
      values.city &&
      values.state &&
      deliveryStatus === "AVAILABLE"
    );
  };

  /* ================= SUBMIT ================= */
  const onFormSubmit = async () => {
    try {
      setProgress({ open: true, text: "Processing..." });

      // COD
      if (paymentType === "COD") {
        await placeOrder(null);
        return;
      }

      // ONLINE → Razorpay
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Failed to load payment gateway");

      const res = await fetch(`${BASE_URL}/api/payment/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: Math.max(1, Math.round(totalPrice * 100)),
        }),
      });

      const data = await res.json();
      if (!data?.id) throw new Error("Payment init failed");

      setProgress({ open: false, text: "" });

      const rzp = new window.Razorpay({
        key: "rzp_live_Ri5411bGqkTbyR",
        amount: data.amount,
        currency: "INR",
        name: "Dropkart",
        description: "Order Payment",
        order_id: data.id,

        handler: async (response: any) => {
          setProgress({ open: true, text: "Confirming order..." });
          await placeOrder(response.razorpay_payment_id);
        },
      });

      rzp.on("payment.failed", (resp: any) => {
        setOrderError(resp?.error?.description || "Payment failed");
      });

      rzp.open();
    } catch (err: any) {
      setOrderError(err.message);
    } finally {
      setProgress({ open: false, text: "" });
    }
  };

  /* ================= SUCCESS / ERROR ================= */
  if (orderPlaced)
    return (
      <Result
        icon={<SmileOutlined style={{ color: "#52c41a" }} />}
        title="Thank you for your order!"
        subTitle={`Order #${orderId}`}
        extra={[
          <Button type="primary" onClick={() => history.push("/")}>
            Continue Shopping
          </Button>,
        ]}
      />
    );

  if (orderError)
    return (
      <Result
        status="error"
        icon={<CloseCircleOutlined />}
        title="Order Failed"
        subTitle={orderError}
        extra={<Button onClick={() => window.location.reload()}>Retry</Button>}
      />
    );

  /* ================= UI ================= */
  return (
    <>
      <Modal open={progress.open} footer={null} closable={false} centered>
        <Spin /> <Typography.Text>{progress.text}</Typography.Text>
      </Modal>

      <div className="order-container">
        <ContentTitle icon={<ShoppingOutlined />} title="Place Your Order" />

        <Row gutter={[20, 20]} justify="center">
          <Col xs={24} md={10}>
            <Card title="Order Summary">
              {perfumes.map((p) => (
                <OrderItem
                  key={p.id}
                  perfume={p}
                  quantity={perfumesFromLocalStorage.get(p.id)}
                />
              ))}
              <Divider />
              <Typography.Title level={4}>₹{totalPrice}</Typography.Title>
            </Card>
          </Col>

          <Col xs={24} md={10}>
            <Card title="Shipping Details">
              <Form
                form={form}
                layout="vertical"
                onFinish={onFormSubmit}
                onValuesChange={(_, all) => {
                  const pincode = all.postIndex;
                  if (pincode?.length === 6) fetchPincodeDetails(pincode);
                }}
              >
                <FormInput title="First Name" name="firstName" titleSpan={8} wrapperSpan={16}/>
                <FormInput title="Last Name" name="lastName"  titleSpan={8} wrapperSpan={16}/>
                <FormInput title="Phone" name="phoneNumber"  titleSpan={8} wrapperSpan={16}/>
                <FormInput title="Email" name="email"  titleSpan={8} wrapperSpan={16}/>
                <FormInput title="Address" name="address"  titleSpan={8} wrapperSpan={16}/>
                <FormInput title="Postal Code" name="postIndex"  titleSpan={8} wrapperSpan={16}/>

                {deliveryStatus === "AVAILABLE" && (
                  <Typography.Text style={{ color: "#52c41a" }}>
                    Delivery available
                  </Typography.Text>
                )}
                {deliveryStatus === "NOT_AVAILABLE" && (
                  <Typography.Text style={{ color: "#ff4d4f" }}>
                    Delivery not available
                  </Typography.Text>
                )}

                <FormInput title="City" name="city" disabled  titleSpan={8} wrapperSpan={16}/>
                <FormInput title="State" name="state" disabled  titleSpan={8} wrapperSpan={16}/>

                <Radio.Group
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                >
                  <Radio value="COD">Cash on Delivery</Radio>
                  <Radio value="ONLINE">Online Payment</Radio>
                </Radio.Group>

                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  disabled={!isFormValid()}
                >
                  Confirm Order
                </Button>
              </Form>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default Order;
